import type { ComplimentaryCareEnrollment } from '../../api/types';
import { Modal } from '../../components/Modal';
import type { ReviewAction } from './useReviewActions';

interface ReviewActionButtonsProps {
  enrollment: ComplimentaryCareEnrollment;
  onAction: (enrollment: ComplimentaryCareEnrollment, action: ReviewAction) => void;
  busy: boolean;

  /** Full-size buttons on the detail page, compact ones in a table row. */
  size?: 'normal' | 'small';
}

/**
 * The decisions available on an enrollment, in one place so a row and the detail page never drift
 * apart. Ordered the same way everywhere: the step that moves the process forward is the primary
 * button and sits last, the one that takes something away is beside it and styled as such.
 */
export function ReviewActionButtons({ enrollment, onAction, busy, size = 'normal' }: ReviewActionButtonsProps) {
  const modifier = size === 'small' ? ' button--small' : '';

  switch (enrollment.status) {
    case 'pendingReview':
      return (
        <>
          <button
            type="button"
            className={`button button--danger${modifier}`}
            disabled={busy}
            onClick={() => onAction(enrollment, 'reject')}
          >
            Reject
          </button>
          <button
            type="button"
            className={`button button--primary${modifier}`}
            disabled={busy}
            onClick={() => onAction(enrollment, 'approve')}
          >
            Approve
          </button>
        </>
      );

    case 'approved':
      return (
        <button
          type="button"
          className={`button button--danger${modifier}`}
          disabled={busy}
          onClick={() => onAction(enrollment, 'deactivate')}
        >
          Deactivate
        </button>
      );

    case 'rejected':
      return (
        <button
          type="button"
          className={`button button--primary${modifier}`}
          disabled={busy}
          onClick={() => onAction(enrollment, 'approve')}
        >
          Approve
        </button>
      );

    case 'deactivated':
      return (
        <button
          type="button"
          className={`button button--primary${modifier}`}
          disabled={busy}
          onClick={() => onAction(enrollment, 'reactivate')}
        >
          Activate
        </button>
      );
  }
}

/**
 * What approving this row would do. A submission never becomes a second live enrollment — it takes
 * the place of the one the employee already has, and the dialog says so before HR commits.
 *
 * A re-enrollment reads differently on purpose: there is nothing live to lose, and what approving
 * actually does is reverse a deactivation HR themselves decided on.
 */
function approvalConsequence(enrollment: ComplimentaryCareEnrollment): string {
  switch (enrollment.kind) {
    case 'change':
      return 'This replaces the enrollment they have now. The one being replaced stops being active.';
    case 'reEnrollment':
      return 'Their previous enrollment was deactivated. Approving this puts them back on complimentary care and replaces that record.';
    case 'enrollment':
      return 'Complimentary care becomes active and the clinic can schedule the first appointments.';
  }
}

interface ReviewConfirmModalProps {
  confirming: { enrollment: ComplimentaryCareEnrollment; action: ReviewAction } | null;
  note: string;
  noteError: string | null;
  busy: boolean;
  onNoteChange: (note: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ReviewConfirmModal({
  confirming,
  note,
  noteError,
  busy,
  onNoteChange,
  onConfirm,
  onCancel,
}: ReviewConfirmModalProps) {
  const action = confirming?.action;
  const isReject = action === 'reject';
  const isApprove = action === 'approve';

  const isReEnrollment = confirming?.enrollment.kind === 'reEnrollment';

  const title = isReject
    ? 'Reject this enrollment'
    : isApprove
      ? isReEnrollment
        ? 'Put this employee back on care'
        : 'Approve this change'
      : 'Deactivate this enrollment';

  return (
    <Modal
      open={confirming !== null}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="button button--quiet" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={isApprove ? 'button button--primary' : 'button button--danger-solid'}
            disabled={busy}
            onClick={onConfirm}
          >
            {isReject
              ? 'Reject'
              : isApprove
                ? isReEnrollment
                  ? 'Approve and reactivate'
                  : 'Approve and replace'
                : 'Deactivate'}
          </button>
        </>
      }
    >
      {confirming && (
        <>
          <p>
            {isReject ? (
              <>
                <strong>{confirming.enrollment.employeeDisplayName}</strong> will see the reason below and
                can submit a corrected form.
              </>
            ) : isApprove ? (
              <>
                {approvalConsequence(confirming.enrollment)} The changes listed on this enrollment become
                what <strong>{confirming.enrollment.employeeDisplayName}</strong> is enrolled for.
              </>
            ) : (
              <>
                Complimentary care for <strong>{confirming.enrollment.employeeDisplayName}</strong> and{' '}
                {confirming.enrollment.enrollees.length - 1} other enrollee(s) stops being active. You can
                activate it again later.
              </>
            )}
          </p>

          {/* Deactivating discards any change still waiting on this enrollment — a decision about a
              benefit that is being switched off is a decision about nothing. HR should know that
              before clicking, not find the row gone afterwards. */}
          {action === 'deactivate' && confirming.enrollment.hasPendingChange && (
            <p className="issues__block issues__block--warning">
              They have a change waiting for review. It is discarded with the enrollment — if they
              should stay enrolled on the changed details, approve that change first.
            </p>
          )}

          <div className="field">
            <label className="field__label" htmlFor="reviewNote">
              {isReject ? 'Reason' : 'Note'} {isReject && <span className="field__required">*</span>}
            </label>
            <textarea
              id="reviewNote"
              className={noteError ? 'control control--invalid' : 'control'}
              rows={3}
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              maxLength={1000}
              placeholder={
                isReject
                  ? 'e.g. the birth certificate for Child #2 is missing'
                  : isApprove
                    ? 'Optional — e.g. marriage certificate checked against the original'
                    : 'Optional — e.g. left the company on 1 October'
              }
            />
            {noteError && <p className="field__error">{noteError}</p>}
          </div>
        </>
      )}
    </Modal>
  );
}
